import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DocumentNotesApi } from '../../services/api/DocumentNotesApi';
import { DocumentApi } from '../../services/api/DocumentApi';
import { marked } from 'marked';
import CourseNoteRichEditor from './CourseNoteRichEditor';
import { useData } from '../../contexts/DataContext';
import useDictation from '../../hooks/useDictation';
import './NoteEditor.css';

// Helper to detect HTML
const looksLikeHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(s || "");

// Helper to convert markdown to HTML
const mdToHtml = (md) => {
  if (!md) return "";
  if (looksLikeHtml(md)) return md;
  return marked.parse(md);
};

/**
 * NoteEditor Component
 * WYSIWYG rich text editor with auto-save, manual save, and delete functionality
 * Uses TipTap for editing, stores as Markdown
 * 
 * @param {Object} props
 * @param {number} props.noteId - Document ID of the note
 * @param {number} props.endeavorId - Course endeavor ID for cache invalidation
 * @param {string} props.initialContent - Initial markdown content
 * @param {Function} props.onSave - Callback after successful save
 * @param {Function} props.onDelete - Callback after successful delete
 * @param {Function} props.onBack - Callback for back navigation
 * @param {boolean} props.readOnly - Read-only mode (default: false)
 */
const NoteEditor = ({ noteId, endeavorId, initialContent = '', onSave, onDelete, onBack, readOnly = false }) => {
  console.log('[NoteEditor] Rendered with TipTap Rich Editor');
  console.log('[NoteEditor] Props:', { noteId, endeavorId, initialContentLength: initialContent?.length, readOnly });
  
  const { invalidateNotesCache } = useData();
  const [savedContent, setSavedContent] = useState(initialContent);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved'
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDraftRecovery, setShowDraftRecovery] = useState(false);
  const [recoveredDraft, setRecoveredDraft] = useState(null);
  
  const editorRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const isExplicitActionRef = useRef(false);

  const dictation = useDictation({ editorRef });

  // Get current markdown from TipTap editor
  const getCurrentMd = useCallback(() => {
    if (!editorRef.current) return savedContent;
    return editorRef.current.storage.markdown.getMarkdown();
  }, [savedContent]);

  // Auto-save handler (separate to avoid closure issues)
  const handleAutoSave = useCallback(async () => {
    if (!editorRef.current) return;
    if (!noteId) return;

    const currentMd = editorRef.current.storage.markdown.getMarkdown();
    const hasChanges = currentMd !== savedContent;

    if (!hasChanges) return;

    console.log('[NoteEditor] Auto-saving note:', noteId);
    setSaveStatus('saving');
    setError(null);

    try {
      await DocumentNotesApi.updateNoteContent(noteId, currentMd);
      console.log('[NoteEditor] Auto-save complete');

      setSavedContent(currentMd);
      setLastSavedTime(new Date());
      setSaveStatus('saved');

      // Clear localStorage draft
      const draftKey = `dh:courseNoteDraft:${noteId}`;
      localStorage.removeItem(draftKey);

      // Invalidate the note content cache so fresh content loads next time
      if (endeavorId && invalidateNotesCache) {
        invalidateNotesCache(endeavorId, noteId);
        console.log('[NoteEditor] Auto-save cache invalidated for endeavor:', endeavorId, 'note:', noteId);
      }

      // Dispatch custom event for document refresh
      window.dispatchEvent(new CustomEvent('documents:changed'));
    } catch (err) {
      console.error('[NoteEditor] Auto-save failed:', err);
      setSaveStatus('error');
      setError('Auto-save failed. Please try again.');
    }
  }, [noteId, endeavorId, savedContent, invalidateNotesCache]);

  // Handle editor ready
  const handleEditorReady = useCallback((editor) => {
    console.log('[NoteEditor] Editor instance ready, storing in editorRef');
    editorRef.current = editor;
    console.log('[NoteEditor] Editor has markdown extension:', !!editor?.storage?.markdown);

    // Set up dirty tracking
    editor.on('update', () => {
      const currentMd = editor.storage.markdown.getMarkdown();
      const isDirty = currentMd !== savedContent;
      setSaveStatus(isDirty ? 'unsaved' : 'saved');

      // Clear existing timers
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      if (!isDirty) return;

      // Debounce: Wait 1000ms after last keystroke to update local draft
      debounceTimerRef.current = setTimeout(() => {
        // Save to localStorage
        if (noteId) {
          const draftKey = `dh:courseNoteDraft:${noteId}`;
          localStorage.setItem(draftKey, currentMd);
          console.log('[NoteEditor] Draft saved to localStorage');
        }

        // Set up auto-save timer for 60 seconds
        autoSaveTimerRef.current = setTimeout(() => {
          if (!editorRef.current) return;
          const latestMd = editorRef.current.storage.markdown.getMarkdown();
          const latestSavedContent = savedContent; // capture current saved content
          const stillDirty = latestMd !== latestSavedContent;
          if (stillDirty) {
            console.log('[NoteEditor] Auto-saving after 60s');
            handleAutoSave();
          }
        }, 60 * 1000); // 60 seconds
      }, 1000); // 1 second debounce
    });
  }, [noteId, savedContent, handleAutoSave]);

  // Theme detection removed (editor styling is token-driven)

  // Update savedContent when initialContent changes
  useEffect(() => {
    setSavedContent(initialContent);
  }, [initialContent]);

  // Check for localStorage draft on mount
  useEffect(() => {
    if (!noteId) return;
    
    const draftKey = `dh:courseNoteDraft:${noteId}`;
    const savedDraft = localStorage.getItem(draftKey);
    
    if (savedDraft && savedDraft !== initialContent && savedDraft !== savedContent) {
      console.log('[NoteEditor] Found unsaved draft in localStorage');
      setRecoveredDraft(savedDraft);
      setShowDraftRecovery(true);
    }
  }, [noteId, initialContent, savedContent]);

  // Load note content if noteId is provided but no initialContent
  useEffect(() => {
    const loadNoteContent = async () => {
      // Skip if no noteId OR if initialContent is explicitly provided (even if empty string)
      if (!noteId || initialContent !== undefined) {
        console.log('[NoteEditor] Skipping load - noteId:', noteId, 'initialContent provided:', initialContent !== undefined);
        return;
      }

      console.log('[NoteEditor] Loading content for noteId:', noteId);
      try {
        const response = await DocumentNotesApi.getNoteContent(noteId);
        console.log('[NoteEditor] Content loaded:', { hasContent: !!response?.content, length: response?.content?.length });
        if (response && response.content) {
          setSavedContent(response.content);
        }
      } catch (err) {
        console.error('[NoteEditor] Failed to load note content:', err);
        setError('Failed to load note content.');
      }
    };

    loadNoteContent();
  }, [noteId, initialContent]);

  // Save function
  const saveNote = useCallback(async ({ finalize = false } = {}) => {
    if (!editorRef.current) {
      console.warn('[NoteEditor] Save called but editor not ready');
      setError('Editor not ready. Please try again.');
      return;
    }
    
    const currentMd = getCurrentMd();
    const hasChanges = currentMd !== savedContent;
    
    console.log('[NoteEditor] Save attempt:', {
      noteId,
      currentMdLength: currentMd.length,
      savedContentLength: savedContent.length,
      hasChanges,
      currentMdPreview: currentMd.substring(0, 100),
      savedContentPreview: savedContent.substring(0, 100)
    });
    
    if (!hasChanges) {
      console.log('[NoteEditor] Save skipped - no changes');
      return;
    }

    // If no noteId exists yet, use onSave callback for first-time creation
    if (!noteId) {
      if (onSave) {
        console.log('[NoteEditor] Creating new note via onSave callback, contentLength:', currentMd.length);
        setSaveStatus('saving');
        setError(null);
        try {
          await onSave(currentMd);
          setSavedContent(currentMd);
          setLastSavedTime(new Date());
          setSaveStatus('saved');
          console.log('[NoteEditor] New note created');
        } catch (err) {
          console.error('[NoteEditor] Failed to create note:', err);
          setError('Failed to create note');
          setSaveStatus('unsaved');
        }
      } else {
        console.log('[NoteEditor] Save skipped - no noteId and no onSave callback');
      }
      return;
    }

    console.log('[NoteEditor] Saving note:', noteId, 'contentLength:', currentMd.length, 'finalize:', finalize);
    setSaveStatus('saving');
    setError(null);

    try {
      if (finalize) {
        // Save-close path: persist via finalize endpoint, then trigger AI re-ingest
        // (markdown-class summary regeneration) when the server says content changed.
        const result = await DocumentNotesApi.finalizeNote(noteId, currentMd);
        console.log('[NoteEditor] Finalize complete:', result);
        if (result?.needsReindex) {
          // Fire-and-forget: don't block the UI on summary regeneration.
          DocumentNotesApi.triggerReindex(noteId).catch(err => {
            console.warn('[NoteEditor] Reindex trigger failed (will retry on next finalize):', err);
          });
        }
      } else {
        await DocumentNotesApi.updateNoteContent(noteId, currentMd);
        console.log('[NoteEditor] Content saved to server');
      }
      
      setSavedContent(currentMd);
      setLastSavedTime(new Date());
      setSaveStatus('saved');
      console.log('[NoteEditor] Save complete, savedContent updated');
      
      // Clear localStorage draft after successful save
      if (noteId) {
        const draftKey = `dh:courseNoteDraft:${noteId}`;
        localStorage.removeItem(draftKey);
        console.log('[NoteEditor] localStorage draft cleared');
      }
      
      // Invalidate the note content cache so fresh content loads next time
      if (endeavorId && invalidateNotesCache) {
        invalidateNotesCache(endeavorId, noteId);
        console.log('[NoteEditor] Cache invalidated for endeavor:', endeavorId, 'note:', noteId);
      }
      
      if (onSave) {
        onSave(currentMd);
      }

      // Dispatch custom event for document refresh
      window.dispatchEvent(new CustomEvent('documents:changed'));
    } catch (err) {
      console.error('[NoteEditor] Failed to save note:', err);
      setError('Failed to save note. Please try again.');
      setSaveStatus('error');
    }
  }, [endeavorId, getCurrentMd, invalidateNotesCache, noteId, onSave, savedContent]);

  // Delete function
  const deleteNote = useCallback(async () => {
    if (!noteId) return;

    try {
      await DocumentApi.deleteDocument(noteId);
      
      if (onDelete) {
        onDelete();
      }

      // Dispatch custom event for document refresh
      window.dispatchEvent(new CustomEvent('documents:changed'));
    } catch (err) {
      console.error('Failed to delete note:', err);
      setError('Failed to delete note. Please try again.');
    }
  }, [noteId, onDelete]);

  // Blur autosave handler
  const handleBlur = useCallback(() => {
    const currentMd = getCurrentMd();
    const isDirty = currentMd !== savedContent;
    if (isDirty && !isExplicitActionRef.current && saveStatus !== 'saving') {
      console.log('[NoteEditor] Blur autosave triggered');
      saveNote();
    }
  }, [getCurrentMd, savedContent, saveStatus, saveNote]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // beforeunload warning when dirty
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const currentMd = getCurrentMd();
      const isDirty = currentMd !== savedContent;
      if (isDirty && saveStatus !== 'saving') {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [getCurrentMd, savedContent, saveStatus]);

  // Format last saved time
  const formatLastSaved = () => {
    if (!lastSavedTime) return 'Never';
    
    const now = new Date();
    const diff = now - lastSavedTime;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    
    if (minutes === 0) {
      return 'Just now';
    } else if (minutes < 60) {
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else {
      return lastSavedTime.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    }
  };

  // Get save status label
  const getSaveStatusLabel = () => {
    if (saveStatus === 'saved') {
      return lastSavedTime ? `Saved ${formatLastSaved()}` : 'Saved';
    } else if (saveStatus === 'unsaved') {
      return 'Unsaved changes';
    } else if (saveStatus === 'saving') {
      return 'Saving…';
    } else if (saveStatus === 'error') {
      return 'Save failed';
    }
    return '';
  };

  // Back button handler
  const handleBackToList = () => {
    if (onBack) {
      onBack();
    }
  };

  // Save button handler — explicit save = save-close = re-summarize
  const handleSave = () => {
    isExplicitActionRef.current = true;
    setTimeout(() => { isExplicitActionRef.current = false; }, 100);
    saveNote({ finalize: true });
  };

  // Delete button handler
  const handleDelete = () => {
    isExplicitActionRef.current = true;
    setTimeout(() => { isExplicitActionRef.current = false; }, 100);
    setShowDeleteConfirm(true);
  };

  // Check if content has changed
  const isDirty = getCurrentMd() !== savedContent;

  return (
    <div className="note-editor">
      <div className="dhNotesFill">
        {/* Draft Recovery Banner */}
        {showDraftRecovery && recoveredDraft && (
          <div className="note-editor__draft-recovery">
            <div className="draft-recovery__message">
              <svg className="icon-info" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M10 6v4M10 14h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Recovered an unsaved draft
            </div>
            <div className="draft-recovery__actions">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  // Load recovered draft into editor
                  if (editorRef.current && recoveredDraft) {
                    const html = mdToHtml(recoveredDraft);
                    editorRef.current.commands.setContent(html, false);
                    setSavedContent(initialContent); // Keep savedContent as original so it shows as dirty
                    setSaveStatus('unsaved');
                  }
                  setShowDraftRecovery(false);
                }}
              >
                Use Draft
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  if (noteId) {
                    localStorage.removeItem(`dh:courseNoteDraft:${noteId}`);
                  }
                  setShowDraftRecovery(false);
                  setRecoveredDraft(null);
                }}
              >
                Discard
              </button>
            </div>
          </div>
        )}
        
        {/* Compact Header Bar */}
        <div className="dhNotesTop noteHeaderBar">
          <div className="noteHeaderTitle">
            {onBack && (
              <button className="noteBackBtn" type="button" onClick={handleBackToList} title="Back to notes list">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
            )}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
            <span>Notes</span>
          </div>

          <div className="noteHeaderRight">
            <div className="noteSaveStatus">
              <span className="statusText">{getSaveStatusLabel()}</span>
              <span className={`statusDot ${saveStatus}`} />
            </div>

            <div className="noteHeaderActions">
              <button
                className="noteSaveBtn"
                type="button"
                onClick={handleSave}
                disabled={!isDirty || saveStatus === 'saving'}
                title={isDirty ? "Save" : "No changes to save"}
              >
                {saveStatus === 'saving' ? "Saving…" : "Save"}
              </button>

              <button
                className="noteDeleteBtn"
                type="button"
                onClick={handleDelete}
                title="Delete note"
              >
                Delete
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="note-editor__error">
            {error}
          </div>
        )}

        {/* TipTap Rich Text Editor */}
        <div className="note-editor__content">
          <CourseNoteRichEditor
            noteId={noteId}
            initialMd={savedContent}
            onReady={handleEditorReady}
            readOnly={readOnly}
            onBlur={handleBlur}
            placeholder="Start taking notes here…"
            dictation={dictation}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Note?</h3>
            <p>Are you sure you want to delete this note? This action cannot be undone.</p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  deleteNote();
                }}
              >
                Delete Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoteEditor;
